import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Autocomplete,
  Card,
  CardContent,
  Stack,
  alpha,
  Divider,
  Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PublicIcon from '@mui/icons-material/Public';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventIcon from '@mui/icons-material/Event';
import TuneIcon from '@mui/icons-material/Tune';
import { getHotelConfiguration, updateHotelConfiguration, getHotelConfigurationOptions } from '../../services/api';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import { CONFIG_DARK, darkSurfaceCardSx } from './configSurface';

const THEME_COLOR = '#4ce1b6';

const CONFIG_FIELDS = [
  {
    key: 'timezone',
    title: 'Timezone',
    subtitle: 'Check-in, reports & ops',
    icon: AccessTimeIcon,
    placeholder: 'Search timezone…',
    emptyText: 'No timezone found',
  },
  {
    key: 'country',
    title: 'Country',
    subtitle: 'Property location',
    icon: PublicIcon,
    placeholder: 'Search country…',
    emptyText: 'No country found',
  },
  {
    key: 'currency',
    title: 'Currency',
    subtitle: 'Rates & payments',
    icon: AttachMoneyIcon,
    placeholder: 'Search currency…',
    emptyText: 'No currency found',
  },
  {
    key: 'date_format',
    title: 'Date format',
    subtitle: 'UI, invoices & PDFs',
    icon: EventIcon,
    placeholder: 'Select date format…',
    emptyText: 'No format found',
    optionsKey: 'date_formats',
  },
];

function AutocompleteDropdownPaper({ isDark, ...paperProps }) {
  return (
    <Paper
      {...paperProps}
      sx={{
        backgroundColor: isDark ? CONFIG_DARK.surface : '#ffffff',
        border: `1px solid ${isDark ? CONFIG_DARK.borderStrong : '#e0e0e0'}`,
        mt: 1,
        boxShadow: isDark
          ? '0 8px 24px rgba(0, 0, 0, 0.4)'
          : '0 4px 12px rgba(0, 0, 0, 0.15)',
        '& .MuiAutocomplete-noOptions': {
          color: isDark ? alpha('#fff', 0.7) : '#757575',
        },
        ...paperProps.sx,
      }}
    />
  );
}

const autocompleteFieldSx = (isDark, hasError) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    color: isDark ? '#fff' : '#212121',
    backgroundColor: isDark ? CONFIG_DARK.elevated : '#fff',
    '& fieldset': {
      borderColor: hasError ? 'error.main' : isDark ? CONFIG_DARK.borderStrong : alpha('#ccc', 0.5),
    },
    '&:hover fieldset': {
      borderColor: hasError ? 'error.main' : THEME_COLOR,
    },
    '&.Mui-focused fieldset': {
      borderColor: THEME_COLOR,
      borderWidth: 2,
    },
    '& .MuiInputBase-input': {
      color: isDark ? '#fff' : '#212121',
    },
  },
  '& .MuiInputLabel-root': {
    color: isDark ? alpha('#fff', 0.7) : '#757575',
  },
  '& .MuiFormHelperText-root': {
    mx: 0,
  },
});

function ConfigFieldBlock({
  title,
  subtitle,
  icon: Icon,
  isDark,
  children,
  onSave,
  saving,
  saved,
}) {
  return (
    <Box
      sx={{
        height: '100%',
        p: 2,
        borderRadius: 2.5,
        border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f766e', 0.12)}`,
        background: isDark
          ? `linear-gradient(160deg, ${alpha('#fff', 0.04)} 0%, ${alpha('#000', 0.12)} 100%)`
          : `linear-gradient(160deg, ${alpha('#fff', 0.95)} 0%, ${alpha(THEME_COLOR, 0.06)} 100%)`,
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          borderColor: alpha(THEME_COLOR, isDark ? 0.35 : 0.45),
          boxShadow: isDark
            ? `0 8px 24px ${alpha('#000', 0.35)}`
            : `0 8px 20px ${alpha('#0f766e', 0.08)}`,
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.25} sx={{ mb: 1.75 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: `linear-gradient(135deg, ${alpha(THEME_COLOR, 0.28)} 0%, ${alpha(THEME_COLOR, 0.08)} 100%)`,
            border: `1px solid ${alpha(THEME_COLOR, 0.25)}`,
          }}
        >
          <Icon sx={{ color: '#0d9488', fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, pt: 0.15 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: isDark ? CONFIG_DARK.text : '#0f172a', lineHeight: 1.2 }}
          >
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: isDark ? CONFIG_DARK.textMuted : '#64748b' }}>
            {subtitle}
          </Typography>
        </Box>
        {saved ? (
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon sx={{ fontSize: '16px !important' }} />}
            label="Saved"
            sx={{
              height: 24,
              fontWeight: 700,
              bgcolor: alpha('#0d9488', isDark ? 0.2 : 0.12),
              color: '#0d9488',
              border: `1px solid ${alpha('#0d9488', 0.35)}`,
              '& .MuiChip-icon': { color: '#0d9488' },
            }}
          />
        ) : null}
      </Stack>

      <Stack spacing={1.25}>
        {children}
        <Button
          fullWidth
          variant="contained"
          disableElevation
          size="small"
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : saved ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
            ) : (
              <SaveIcon sx={{ fontSize: 18 }} />
            )
          }
          onClick={onSave}
          disabled={saving}
          sx={{
            py: 0.85,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.8125rem',
            borderRadius: 2,
            border: `1px solid ${saved ? '#0d9488' : alpha(THEME_COLOR, 0.55)}`,
            backgroundColor: saved ? '#0d9488' : THEME_COLOR,
            color: '#0f172a',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: saved ? '#0f766e' : '#3dd1a3',
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: alpha(THEME_COLOR, 0.45),
              color: alpha('#0f172a', 0.55),
            },
          }}
        >
          {saving ? 'Saving…' : saved ? 'Saved' : 'Save'}
        </Button>
      </Stack>
    </Box>
  );
}

const HotelConfiguration = () => {
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';

  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState(null);
  const [config, setConfig] = useState({ timezone: '', country: '', currency: '', date_format: '' });
  const [options, setOptions] = useState({ timezones: [], countries: [], currencies: [], date_formats: [] });
  const [errors, setErrors] = useState({});
  const [fieldSaved, setFieldSaved] = useState({});
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [configData, optionsData] = await Promise.all([
          getHotelConfiguration(),
          getHotelConfigurationOptions(),
        ]);
        setConfig({
          timezone: configData.timezone || '',
          country: configData.country || '',
          currency: configData.currency || '',
          date_format: configData.date_format || 'DD/MM/YYYY',
        });
        setOptions(optionsData);
      } catch {
        setLoadError('Failed to load hotel configuration. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    return watchThemeChanges(setCurrentTheme);
  }, []);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setConfig((prev) => ({ ...prev, [field]: value }));
    setFieldSaved((prev) => ({ ...prev, [field]: false }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSaveField = useCallback(
    async (field) => {
      const value = config[field];
      if (!value) {
        setErrors((prev) => ({ ...prev, [field]: 'Please select a value before saving.' }));
        return;
      }

      setSavingField(field);
      setFieldSaved((prev) => ({ ...prev, [field]: false }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });

      try {
        await updateHotelConfiguration({ [field]: value });
        if (window.DJANGO_CONTEXT?.selectedHotel) {
          window.DJANGO_CONTEXT.selectedHotel[field] = value;
        }
        setFieldSaved((prev) => ({ ...prev, [field]: true }));
        window.dispatchEvent(new CustomEvent('hotel-config-updated'));
        window.setTimeout(() => {
          setFieldSaved((prev) => ({ ...prev, [field]: false }));
        }, 2500);
      } catch (error) {
        if (error.errors?.[field]) {
          const msg = Array.isArray(error.errors[field]) ? error.errors[field][0] : error.errors[field];
          setErrors((prev) => ({ ...prev, [field]: msg }));
        } else {
          setErrors((prev) => ({
            ...prev,
            [field]: error.error || 'Failed to save. Please try again.',
          }));
        }
      } finally {
        setSavingField(null);
      }
    },
    [config]
  );

  const getOptionByValue = (optionsList, value) =>
    optionsList.find((opt) => opt.value === value) || null;

  const getOptionsForField = (fieldDef) => {
    const key = fieldDef.optionsKey || `${fieldDef.key}s`.replace('countrys', 'countries');
    if (fieldDef.key === 'country') return options.countries || [];
    if (fieldDef.key === 'currency') return options.currencies || [];
    if (fieldDef.key === 'timezone') return options.timezones || [];
    if (fieldDef.key === 'date_format') return options.date_formats || [];
    return options[key] || [];
  };

  const renderAutocomplete = (field, optionList, placeholder, noOptionsText) => (
    <Autocomplete
      options={optionList}
      getOptionLabel={(option) => option.label || ''}
      value={getOptionByValue(optionList, config[field])}
      onChange={(event, newValue) => {
        handleChange(field)({ target: { value: newValue?.value || '' } });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          error={!!errors[field]}
          helperText={errors[field]}
          sx={autocompleteFieldSx(isDark, !!errors[field])}
        />
      )}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{
            py: 1.5,
            px: 2,
            color: isDark ? '#fff' : '#212121',
            '&:hover': { backgroundColor: alpha(THEME_COLOR, 0.1) },
            '&[aria-selected="true"]': { backgroundColor: alpha(THEME_COLOR, 0.15) },
          }}
        >
          <Typography variant="body2" sx={{ color: 'inherit' }}>
            {option.label}
          </Typography>
        </Box>
      )}
      noOptionsText={noOptionsText}
      PaperComponent={(props) => <AutocompleteDropdownPaper isDark={isDark} {...props} />}
      sx={{
        '& .MuiAutocomplete-inputRoot': {
          backgroundColor: isDark ? CONFIG_DARK.elevated : '#fff',
          color: isDark ? '#fff' : '#212121',
        },
      }}
    />
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress sx={{ color: THEME_COLOR }} />
      </Box>
    );
  }

  return (
    <Box>
      {loadError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setLoadError('')}>
          {loadError}
        </Alert>
      )}

      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          ...(isDark
            ? {
                ...darkSurfaceCardSx,
                boxShadow: `0 1px 0 ${alpha('#fff', 0.06)} inset, 0 16px 40px ${alpha('#000', 0.45)}`,
              }
            : {
                bgcolor: '#ffffff',
                border: '1px solid #e2e8f0',
                boxShadow: `0 12px 40px ${alpha('#0f766e', 0.08)}`,
              }),
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2.25,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: `1px solid ${isDark ? alpha('#fff', 0.08) : '#e2e8f0'}`,
            background: isDark
              ? `linear-gradient(90deg, ${alpha(THEME_COLOR, 0.14)} 0%, ${alpha('#fff', 0.02)} 55%, transparent 100%)`
              : `linear-gradient(90deg, ${alpha(THEME_COLOR, 0.12)} 0%, ${alpha('#f8fafc', 0.9)} 55%, #fff 100%)`,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${THEME_COLOR} 0%, #0d9488 100%)`,
              boxShadow: `0 8px 20px ${alpha(THEME_COLOR, 0.35)}`,
            }}
          >
            <TuneIcon sx={{ color: '#0f172a', fontSize: 26 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: isDark ? CONFIG_DARK.text : '#0f172a' }}>
              Regional & display settings
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? CONFIG_DARK.textMuted : '#64748b', mt: 0.25 }}>
              Timezone, locale, currency, and date format for your property
            </Typography>
          </Box>
          <Chip
            label="Per-field save"
            size="small"
            sx={{
              display: { xs: 'none', sm: 'flex' },
              fontWeight: 700,
              bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#0f766e', 0.08),
              color: isDark ? alpha('#fff', 0.75) : '#0f766e',
              border: `1px solid ${alpha(THEME_COLOR, 0.25)}`,
            }}
          />
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2}>
            {CONFIG_FIELDS.map((fieldDef) => (
              <Grid item xs={12} sm={6} lg={3} key={fieldDef.key}>
                <ConfigFieldBlock
                  title={fieldDef.title}
                  subtitle={fieldDef.subtitle}
                  icon={fieldDef.icon}
                  isDark={isDark}
                  onSave={() => handleSaveField(fieldDef.key)}
                  saving={savingField === fieldDef.key}
                  saved={fieldSaved[fieldDef.key]}
                >
                  {renderAutocomplete(
                    fieldDef.key,
                    getOptionsForField(fieldDef),
                    fieldDef.placeholder,
                    fieldDef.emptyText,
                  )}
                </ConfigFieldBlock>
              </Grid>
            ))}
          </Grid>
        </CardContent>

        <Divider sx={{ borderColor: isDark ? alpha('#fff', 0.08) : '#e2e8f0' }} />

        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          <Typography variant="body2" sx={{ color: isDark ? CONFIG_DARK.textMuted : '#64748b', lineHeight: 1.7 }}>
            <Box component="span" sx={{ fontWeight: 700, color: isDark ? CONFIG_DARK.text : '#334155' }}>
              Tip:
            </Box>{' '}
            Save each field after changing it. Date format applies across booking screens, lists, and invoices;
            timezone drives check-in/out times and daily reports.
          </Typography>
        </Box>
      </Card>
    </Box>
  );
};

export default HotelConfiguration;
